import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { gql } from "@apollo/client";
import get from "lodash/get";
import { useTransition, animated } from "react-spring";

import isRouteMatch from "shared/util/isRouteMatch";
import Context from "shared/util/Context";
import SlotMachine, { BANNER_TYPE } from "shared/util/SlotMachine";
import { useMeasure } from "shared/components/Measured";

import LeaderboardAd from "./LeaderboardAd";
import IntoYou from "./IntoYou";
import Promos from "./Promos";
import Incognito from "./Incognito";
import BillingWoes from "./BillingWoes";
import {
	ROUTES,
	ROUTE_LIST,
} from "routers/shared/util/consts";

import "./index.scss";

const NO_BANNER_ROUTES = [
	...ROUTES.AccountReactivate,
	...ROUTES.GDPR,
	...ROUTES.Logout,
	...ROUTES.LegalPurchaseTerms,
	...ROUTES.Offboarding,
	...ROUTES.Onboarding,
	...ROUTES.PasswordReboarding,
	...ROUTES.PhoneWall,
	...ROUTES.RealName,
	...ROUTES.Signup,
	...ROUTES.Splash,
	...ROUTES.StandaloneConversation,
	...ROUTES.Upgrade,
];

// Banner Components to render for each type.
const BANNER = {
	[BANNER_TYPE.BillingWoes]: BillingWoes,
	[BANNER_TYPE.Incognito]: Incognito,
	[BANNER_TYPE.Promos]: Promos,
	[BANNER_TYPE.IntoYou]: IntoYou,
	[BANNER_TYPE.Ad]: LeaderboardAd,
};

// Handles getting the banner to show from SlotMachine,
// as well as recording when it's been rendered.
function useSlotMachine(location, user, session) {
	// Get a banner type to show.
	const [bannerType, setBannerType] = useState(null);
	useEffect(() => {
		const isLoggedIn = !!(user && user.id);
		const isBoosting = !!(user && user.activeBoost);
		const is404 = !isRouteMatch(location, ROUTE_LIST);
		const shouldShowBanner = (
			isLoggedIn && !NO_BANNER_ROUTES.includes(location.pathname) && !isBoosting && !is404
		);

		// Early exit, no need to show a banner here.
		if (!shouldShowBanner) {
			setBannerType(null);

			return;
		}

		const type = SlotMachine.getBannerType({
			isOnHomepage: ROUTES.Doubletake.includes(location.pathname),
			isAlist: !!Context.get("isAlist"),
			isAdFree: !!Context.get("adFree"),
			hasIncognito: !!get(user, "hasIncognito"),
			gatekeeperChecks: get(session, "gatekeeperChecks") || {},
			joinDate: get(user, "joinDate"),
		});

		setBannerType(type);
	}, [location, session, user]);

	// Record that we saw a banner.
	useEffect(() => {
		if (!bannerType) {
			return;
		}

		SlotMachine.recordRender(bannerType);
	}, [bannerType]);

	// Calculate the height of the banner.
	const [ref, { height = 0 }] = useMeasure();
	const [bannerHeight, setBannerHeight] = useState(0);
	useEffect(() => {
		// No banner, set to zero.
		if (!bannerType) {
			setBannerHeight(0);
			return;
		}

		// If we're showing a banner, we _should_ have a height.
		// This `if` ensures we maintain the last height while
		// it's being calculated.
		if (height) {
			setBannerHeight(height);
		}
	}, [height, bannerType]);

	const Banner = BANNER[bannerType];
	return {
		type: bannerType,
		height: bannerHeight,
		render: () => Banner && <Banner ref={ref} />,
	};
}

const HeaderBanner = ({ location, user, session }) => {
	// Get the appropriate banner to show (if any).
	const bannerData = useSlotMachine(location, user, session);

	// Handles height transitions when the type changes.
	const transitions = useTransition(bannerData, () => bannerData.type, {
		from: { height: 0 },
		enter: { height: bannerData.height },
		leave: { height: 0 },
		update: { height: bannerData.height },
	});

	return transitions.map(({ item: { render }, key, props }) => (
		<animated.div key={key} id="leaderboard_container" style={props}>
			{render()}
		</animated.div>
	));
};

HeaderBanner.fragments = {
	bannerUserData: gql`
		fragment BannerUserData on User {
			id
			activeBoost {
				id
				startTime
			}
			hasIncognito
			joinDate
		}
	`,
	bannerSessionData: gql`
		fragment BannerSessionData on Session {
			gatekeeperChecks {
				BILLING_WOES
				RESUB_FIVE_TOKENS
				INTOYOU_MASTHEAD
				ALIST_DISCOUNT_MASTHEAD
			}
		}
	`,
};

HeaderBanner.propTypes = {
	location: PropTypes.object.isRequired,
	user: PropTypes.shape({
		id: PropTypes.string,
		activeBoost: PropTypes.object,
	}),
	session: PropTypes.shape({
		gatekeeperChecks: PropTypes.object,
	}),
};

HeaderBanner.defaultProps = {
	user: null,
	session: null,
};

export default HeaderBanner;



// WEBPACK FOOTER //
// ./src/routers/Desktop/HeaderBanner/index.jsx